import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/**
 * Active outreach sprints — one per activated strategy (path) for a role.
 * Decoupled from saved jobs: a role can have several coexisting sprints
 * (referral + direct), and activation works whether or not the job is saved.
 */
export const activateSprint = createAsyncThunk(
    "activeSprints/activate",
    async (payload, thunkAPI) => {
        try {
            const res = await api.post("/api/activesprints/activate", payload);
            return res.data?.sprint || null;
        } catch (err) {
            return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

export const fetchActiveSprints = createAsyncThunk(
    "activeSprints/fetch",
    async (userId, thunkAPI) => {
        try {
            const res = await api.get(`/api/activesprints/${userId}`);
            return res.data?.sprints || [];
        } catch (err) {
            return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

export const removeActiveSprint = createAsyncThunk(
    "activeSprints/remove",
    async (id, thunkAPI) => {
        try {
            await api.delete(`/api/activesprints/${id}`);
            return id;
        } catch (err) {
            return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
        }
    }
);

const activeSprintSlice = createSlice({
    name: "activeSprints",
    initialState: {
        sprints: [],
        loading: false,
        fetched: false,
        error: null
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchActiveSprints.pending, (state) => { state.loading = true; })
            .addCase(fetchActiveSprints.fulfilled, (state, action) => {
                state.loading = false;
                state.fetched = true;
                state.sprints = Array.isArray(action.payload) ? action.payload : [];
            })
            .addCase(fetchActiveSprints.rejected, (state, action) => {
                state.loading = false;
                state.fetched = true;
                state.error = action.payload || action.error?.message || null;
            })
            .addCase(activateSprint.fulfilled, (state, action) => {
                const sprint = action.payload;
                if (!sprint) return;
                const idx = state.sprints.findIndex((s) => String(s._id) === String(sprint._id));
                if (idx >= 0) state.sprints[idx] = sprint;
                else state.sprints.unshift(sprint);
            })
            .addCase(removeActiveSprint.fulfilled, (state, action) => {
                state.sprints = state.sprints.filter((s) => String(s._id) !== String(action.payload));
            });
    }
});

export default activeSprintSlice.reducer;
